'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function InterestRateCalculator() {
  const t = useTranslations("tools.interestRateCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    loanAmount: 32000,
    loanYears: 3,
    loanMonths: 0,
    monthlyPayment: 960
  });

  const [results, setResults] = useState<{
    interestRate: number;
    totalPayments: number;
    totalInterest: number;
    numPayments: number;
    schedule: Array<{
      month: number;
      payment: number;
      principal: number;
      interest: number;
      balance: number;
    }>;
  } | null>(null);

  const [error, setError] = useState('');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Calculate interest rate using Newton-Raphson method
  const calculateInterestRate = (principal: number, monthlyPayment: number, totalMonths: number): number | null => {
    if (totalMonths === 0 || monthlyPayment === 0) {
      return null;
    }

    const totalPayment = monthlyPayment * totalMonths;
    if (totalPayment <= principal) {
      return 0;
    }

    let low = 0;
    let high = 1;
    let rate = 0.1;
    const tolerance = 0.000001;
    const maxIterations = 1000;

    for (let i = 0; i < maxIterations; i++) {
      const monthlyRate = rate / 12;
      let presentValue;

      if (monthlyRate === 0) {
        presentValue = monthlyPayment * totalMonths;
      } else {
        presentValue = monthlyPayment * (1 - Math.pow(1 + monthlyRate, -totalMonths)) / monthlyRate;
      }

      const diff = presentValue - principal;

      if (Math.abs(diff) < tolerance) {
        return rate * 100;
      }

      if (diff > 0) {
        low = rate;
        rate = (rate + high) / 2;
      } else {
        high = rate;
        rate = (rate + low) / 2;
      }
    }

    return rate * 100;
  };

  // Generate amortization schedule
  const generateAmortizationSchedule = (principal: number, annualRate: number, monthlyPayment: number, totalMonths: number) => {
    const schedule = [];
    let balance = principal;
    const monthlyRate = annualRate / 100 / 12;

    for (let month = 1; month <= totalMonths; month++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      balance -= principalPayment;

      if (balance < 0) balance = 0;

      schedule.push({
        month: month,
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: balance
      });
    }

    return schedule;
  };

  // Handle calculation
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const totalMonths = inputs.loanYears * 12 + inputs.loanMonths;

    if (totalMonths === 0) {
      setError(t('errorInvalidTerm'));
      return;
    }

    if (inputs.monthlyPayment <= 0) {
      setError(t('errorInvalidPayment'));
      return;
    }

    const minPayment = inputs.loanAmount / totalMonths;
    if (inputs.monthlyPayment < minPayment) {
      setError(t('errorPaymentTooLow'));
      return;
    }

    const interestRate = calculateInterestRate(inputs.loanAmount, inputs.monthlyPayment, totalMonths);

    if (interestRate === null) {
      setError(t('errorCalculationFailed'));
      return;
    }

    const totalPayments = inputs.monthlyPayment * totalMonths;
    const totalInterest = totalPayments - inputs.loanAmount;
    const schedule = generateAmortizationSchedule(inputs.loanAmount, interestRate, inputs.monthlyPayment, totalMonths);

    setResults({
      interestRate,
      totalPayments,
      totalInterest,
      numPayments: totalMonths,
      schedule
    });
  };

  // Handle clear
  const handleClear = () => {
    setInputs({
      loanAmount: 32000,
      loanYears: 3,
      loanMonths: 0,
      monthlyPayment: 960
    });
    setResults(null);
    setError('');
  };

  // Colors
  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueLight: '#eff6ff',
    orange: '#f59e0b',
    error: '#dc2626',
    errorBg: '#fef2f2',
  };

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1000px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const formCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', color: colors.textSecondary, fontWeight: 600 },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: `2px solid #e2e8f0`,
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const inputRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: `linear-gradient(135deg, ${colors.blue} 0%, #7c3aed 100%)`,
      color: 'white',
      minHeight: '44px',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
      minHeight: '44px',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const errorMessageStyle = {
    background: colors.errorBg,
    color: colors.error,
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    borderLeft: `4px solid ${colors.error}`,
    fontSize: '0.95rem'
  };

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    marginBottom: '0.75rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #e2e8f0'
  };

  const resultItemHighlightStyle = {
    ...resultItemStyle,
    background: `linear-gradient(135deg, ${colors.blueLight} 0%, #dbeafe 100%)`,
    borderLeft: `4px solid ${colors.blue}`
  };

  const resultLabelStyle = getResponsiveStyle(
    { color: '#475569', fontWeight: 500 },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const resultValueHighlightStyle = getResponsiveStyle(
    { color: colors.blue, fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const chartsSectionStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginTop: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const chartCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.5rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const h4Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', textAlign: 'center' as const },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { marginLeft: '1.5rem', color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem',
    lineHeight: 1.6
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.875rem'
  };

  const thStyle = {
    background: '#f1f5f9',
    padding: '0.75rem 0.5rem',
    textAlign: 'left' as const,
    fontWeight: 600,
    color: '#475569',
    position: 'sticky' as const,
    top: 0
  };

  const tdStyle = {
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #e2e8f0',
    color: '#334155'
  };

  const amortizationTableStyle = {
    maxHeight: '400px',
    overflowY: 'auto' as const,
    marginTop: '1rem'
  };

  // Render pie chart using Canvas
  const renderPieChart = () => {
    if (!results) return null;

    const canvasRef = (canvas: HTMLCanvasElement | null) => {
      if (!canvas || !results) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = 100;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const total = inputs.loanAmount + results.totalInterest;
      const principalAngle = (inputs.loanAmount / total) * 2 * Math.PI;

      // Draw principal slice
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, -Math.PI / 2, -Math.PI / 2 + principalAngle);
      ctx.closePath();
      ctx.fillStyle = colors.blue;
      ctx.fill();

      // Draw interest slice
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, -Math.PI / 2 + principalAngle, 3 * Math.PI / 2);
      ctx.closePath();
      ctx.fillStyle = colors.orange;
      ctx.fill();

      // Draw labels
      ctx.font = 'bold 14px Arial';
      ctx.fillStyle = colors.textPrimary;
      ctx.textAlign = 'center';

      const principalPercent = ((inputs.loanAmount / total) * 100).toFixed(1);
      const interestPercent = ((results.totalInterest / total) * 100).toFixed(1);

      ctx.fillText(`${t('principal')}: ${principalPercent}%`, centerX, centerY + 140);
      ctx.fillText(`${t('interest')}: ${interestPercent}%`, centerX, centerY + 160);

      // Draw legend boxes
      ctx.fillStyle = colors.blue;
      ctx.fillRect(centerX - 80, centerY + 125, 15, 15);
      ctx.fillStyle = colors.orange;
      ctx.fillRect(centerX - 80, centerY + 145, 15, 15);
    };

    return <canvas ref={canvasRef} width="300" height="300" />;
  };

  // Render amortization table
  const renderAmortizationTable = () => {
    if (!results) return null;

    const displaySchedule = results.schedule.filter((_, index) =>
      index % 6 === 0 || index === results.schedule.length - 1
    );

    return (
      <div style={amortizationTableStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>{t('month')}</th>
              <th style={thStyle}>{t('payment')}</th>
              <th style={thStyle}>{t('principal')}</th>
              <th style={thStyle}>{t('interest')}</th>
              <th style={thStyle}>{t('balance')}</th>
            </tr>
          </thead>
          <tbody>
            {displaySchedule.map((row, index) => (
              <tr key={index}>
                <td style={tdStyle}>{row.month}</td>
                <td style={tdStyle}>${row.payment.toFixed(2)}</td>
                <td style={tdStyle}>${row.principal.toFixed(2)}</td>
                <td style={tdStyle}>${row.interest.toFixed(2)}</td>
                <td style={tdStyle}>${row.balance.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
          </div>

          <div style={calculatorLayoutStyle}>
            {/* Form */}
            <div style={formCardStyle}>
              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label htmlFor="loan-amount" style={labelStyle}>{t('loanAmountLabel')}</label>
                  <input
                    type="number"
                    id="loan-amount"
                    step="0.01"
                    min="0"
                    value={inputs.loanAmount}
                    onChange={(e) => setInputs({...inputs, loanAmount: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('loanTermLabel')}</label>
                  <div style={inputRowStyle}>
                    <div>
                      <input
                        type="number"
                        id="loan-years"
                        min="0"
                        max="50"
                        value={inputs.loanYears}
                        onChange={(e) => setInputs({...inputs, loanYears: parseInt(e.target.value) || 0})}
                        placeholder={t('yearsPlaceholder')}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        id="loan-months"
                        min="0"
                        max="11"
                        value={inputs.loanMonths}
                        onChange={(e) => setInputs({...inputs, loanMonths: parseInt(e.target.value) || 0})}
                        placeholder={t('monthsPlaceholder')}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="monthly-payment" style={labelStyle}>{t('monthlyPaymentLabel')}</label>
                  <input
                    type="number"
                    id="monthly-payment"
                    step="0.01"
                    min="0"
                    value={inputs.monthlyPayment}
                    onChange={(e) => setInputs({...inputs, monthlyPayment: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                </div>

                <div style={buttonGroupStyle}>
                  <button
                    type="submit"
                    style={calculateButtonStyle}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    {t('calculateButton')}
                  </button>
                  <button
                    type="button"
                    onClick={handleClear}
                    style={clearButtonStyle}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
                  >
                    {t('clearButton')}
                  </button>
                </div>
              </form>
            </div>

            {/* Results */}
            {(results || error) && (
              <div style={formCardStyle}>
                <h3 style={h2Style}>{t('resultsTitle')}</h3>

                {error && (
                  <div style={errorMessageStyle}>{error}</div>
                )}

                {results && (
                  <>
                    <div style={resultItemHighlightStyle}>
                      <span style={resultLabelStyle}>{t('interestRateLabel')}</span>
                      <span style={resultValueHighlightStyle}>{results.interestRate.toFixed(3)}%</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('totalPaymentsLabel')}</span>
                      <span style={resultValueStyle}>${results.totalPayments.toFixed(2)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('totalInterestLabel')}</span>
                      <span style={resultValueStyle}>${results.totalInterest.toFixed(2)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('numPaymentsLabel')}</span>
                      <span style={resultValueStyle}>{results.numPayments}</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Charts */}
          {results && (
            <div style={chartsSectionStyle}>
              <div style={chartCardStyle}>
                <h4 style={h4Style}>{t('paymentBreakdownTitle')}</h4>
                {renderPieChart()}
              </div>

              <div style={chartCardStyle}>
                <h4 style={h4Style}>{t('amortizationScheduleTitle')}</h4>
                {renderAmortizationTable()}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToUseStep1')}</li>
          <li style={listItemStyle}>{t('howToUseStep2')}</li>
          <li style={listItemStyle}>{t('howToUseStep3')}</li>
          <li style={listItemStyle}>{t('howToUseStep4')}</li>
          <li style={listItemStyle}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <h3 style={{...h4Style, textAlign: 'left', marginTop: '1.5rem'}}>{t('insightsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>

        <h3 style={{...h4Style, textAlign: 'left', marginTop: '1.5rem'}}>{t('insightsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>

        <h3 style={{...h4Style, textAlign: 'left', marginTop: '1.5rem'}}>{t('insightsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>

        <h3 style={{...h4Style, textAlign: 'left', marginTop: '1.5rem'}}>{t('insightsSubtitle4')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightsBullet1')}</li>
          <li style={listItemStyle}>{t('insightsBullet2')}</li>
          <li style={listItemStyle}>{t('insightsBullet3')}</li>
          <li style={listItemStyle}>{t('insightsBullet4')}</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('bestPractice1Title')}</strong> {t('bestPractice1Text')}</li>
          <li style={listItemStyle}><strong>{t('bestPractice2Title')}</strong> {t('bestPractice2Text')}</li>
          <li style={listItemStyle}><strong>{t('bestPractice3Title')}</strong> {t('bestPractice3Text')}</li>
          <li style={listItemStyle}><strong>{t('bestPractice4Title')}</strong> {t('bestPractice4Text')}</li>
          <li style={listItemStyle}><strong>{t('bestPractice5Title')}</strong> {t('bestPractice5Text')}</li>
          <li style={listItemStyle}><strong>{t('bestPractice6Title')}</strong> {t('bestPractice6Text')}</li>
          <li style={listItemStyle}><strong>{t('bestPractice7Title')}</strong> {t('bestPractice7Text')}</li>
        </ul>
      </section>

      {/* Use Cases */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('useCasesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('useCase1')}</li>
          <li style={listItemStyle}>{t('useCase2')}</li>
          <li style={listItemStyle}>{t('useCase3')}</li>
          <li style={listItemStyle}>{t('useCase4')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{...h4Style, textAlign: 'left', marginTop: '1.5rem'}}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{...h4Style, textAlign: 'left', marginTop: '1.5rem'}}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{...h4Style, textAlign: 'left', marginTop: '1.5rem'}}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{...h4Style, textAlign: 'left', marginTop: '1.5rem'}}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{...h4Style, textAlign: 'left', marginTop: '1.5rem'}}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://brightseotools.com/simple-interest-calculator" target="_blank" rel="noopener noreferrer" style={{color: colors.primary}}>
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.stash.com/learn/the-best-compound-interest-calculators-online/" target="_blank" rel="noopener noreferrer" style={{color: colors.primary}}>
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://elementor.com/tools/interest-calculator/" target="_blank" rel="noopener noreferrer" style={{color: colors.primary}}>
              {t('reference3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.bankrate.com/banking/savings/simple-savings-calculator/" target="_blank" rel="noopener noreferrer" style={{color: colors.primary}}>
              {t('reference4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.nerdwallet.com/banking/calculators/compound-interest-calculator" target="_blank" rel="noopener noreferrer" style={{color: colors.primary}}>
              {t('reference5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}